import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode
} from "react";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import TokenService from "../Services/authentication/token.service";
import { login as loginService } from "../Services/Users.service";
import { ENV_CONFIG } from "../config/envConfig";
import { useDispatch } from "react-redux";
import { login as loginAction, logout as logoutAction } from "../Login/authslice";

// Types and Interfaces
interface DecodedToken {
  exp: number;
  unique_name: string;
  email?: string;
  role?: string;
  userId?: string;
  [key: string]: any;
}

interface AuthContextType {
  token: string | null;
  refreshToken: string | null;
  user: DecodedToken | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (jwt: string, refreshTokenValue?: string) => void;
  logout: () => void;
  refresh: () => Promise<void>;
}

// Create Context
export const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Login function
  const login = useCallback((jwt: string, refreshTokenValue?: string) => {
    TokenService.addOrUpdateLocalAccessToken(jwt);
    if (refreshTokenValue) {
      TokenService.addOrUpdateLocalRefreshToken(refreshTokenValue);
      setRefreshToken(refreshTokenValue);
    }
    
    try {
      const decoded = jwtDecode<DecodedToken>(jwt);
      setToken(jwt);
      setUser(decoded);
      setIsAuthenticated(true);
      setLoading(false);

      // Update Redux store
      const email = decoded.email || decoded.unique_name || "";
      const role = decoded.role || "";
      localStorage.setItem("loginemail", email);
      localStorage.setItem("userRole", role);
      localStorage.setItem("userId", decoded.userId || email);
      
      dispatch(loginAction({ email, role }));
      
      // Fetch user permissions
      if (email) {
      }
    } catch (error) {
      logout();
    }
  }, [dispatch]);

  // Logout function
  const logout = useCallback(() => {
    TokenService.clearAllTokens();
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    
    dispatch(logoutAction());
    
    const logoutUrl = ENV_CONFIG.logoutUrl;
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    } else {
      // Fallback to reload if no logout URL is configured
      window.location.reload();
    }
  }, [dispatch]);

  // Refresh token function
  const refresh = useCallback(async () => {
    try {
      const currentRefreshToken = TokenService.getLocalRefreshToken();
      
      if (currentRefreshToken) {
        // We have a refresh token, use the RefreshAccessToken API
        try {
          const response = await fetch(`${ENV_CONFIG.apiBaseUrl}/Auth/RefreshAccessToken`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(currentRefreshToken)
          });

          if (response.ok) {
            const data = await response.json();
            if (data?.Data?.accessToken) {
              login(data.Data.accessToken, data.Data.refreshToken);
              return;
            }
          }
        } catch (error) {
        }
      }

      // No refresh token available or refresh failed
      // For active users, we'll redirect to re-authenticate
      logout();
      
    } catch (error) {
      logout();
    }
  }, [login, logout]);

  // Handle OAuth redirect and token from URL
  useEffect(() => {
    // Check for various possible token parameters
    const urlParams = new URLSearchParams(location.search);
    const accessToken = urlParams.get("at") || urlParams.get("access_token");
    const referenceToken = urlParams.get("ref") || urlParams.get("reference") || urlParams.get("referencetoken");
    
    // If we have an access token directly, use it
    if (accessToken) {
      login(accessToken);
      
      // Clean up URL parameters
      urlParams.delete("at");
      urlParams.delete("access_token");
      const newParams: { [key: string]: string } = {};
      urlParams.forEach((value: string, key: string) => {
        newParams[key] = value;
      });

      setSearchParams(newParams);
      navigate(
        {
          search: createSearchParams(newParams).toString()
        },
        { replace: true }
      );
      return;
    }
    
    // If we have a reference token, use it to get the actual access token
    if (referenceToken) {
      const userCredentials = {
        grantType: 'form',
        refreshToken: '',
        reference: referenceToken,
        referencetoken: referenceToken
      };

      loginService(userCredentials).then((response) => {
        if (response && typeof response === 'object' && response.status === "success") {
          if (response.accessToken) {
            login(response.accessToken, response.refreshToken);
            
            // Clean up URL parameters
            urlParams.delete("ref");
            urlParams.delete("reference");
            urlParams.delete("referencetoken");
            const newParams: { [key: string]: string } = {};
            urlParams.forEach((value: string, key: string) => {
              newParams[key] = value;
            });

            setSearchParams(newParams);
            navigate(
              {
                search: createSearchParams(newParams).toString()
              },
              { replace: true }
            );
          }
        } else {
          checkStoredTokens();
        }
      }).catch((error) => {
        checkStoredTokens();
      });
      return;
    }
    
    checkStoredTokens();
    
    function checkStoredTokens() {
      // Check for existing token in localStorage
      const storedToken = TokenService.getLocalAccessToken();
      const storedRefresh = TokenService.getLocalRefreshToken();

      if (storedToken) {
        try {
          const decoded = jwtDecode<DecodedToken>(storedToken);
          
          // Check if token is still valid
          if (decoded.exp * 1000 > Date.now()) {
            setToken(storedToken);
            setUser(decoded);
            setIsAuthenticated(true);
            
            // Update Redux store from existing token
            const email = decoded.email || decoded.unique_name || localStorage.getItem("loginemail") || "";
            const role = decoded.role || localStorage.getItem("userRole") || "";
            
            if (email) {
              dispatch(loginAction({ email, role }));
            }
          } else if (storedRefresh) {
            // Token expired but we have refresh token
            setRefreshToken(storedRefresh);
            refresh().catch((error) => {
              handleAuthenticationFailure();
            });
          } else {
            // Token expired and no refresh token
            handleAuthenticationFailure();
          }
        } catch (error) {
          handleAuthenticationFailure();
        }
      } else {
        handleAuthenticationFailure();
      }

      if (storedRefresh) {
        setRefreshToken(storedRefresh);
      }

      setLoading(false);
    }
    
    function handleAuthenticationFailure() {
      // Redirect to logout/login URL
      const logoutUrl = ENV_CONFIG.logoutUrl || "https://cnx-legacy-enterprise-portal-qa.azurewebsites.net/Account/Login?Logout=1";
      window.location.href = logoutUrl;
    }
  }, [location.search, searchParams, setSearchParams, navigate, login, logout, refresh, dispatch]);

  // Auto refresh token before expiration
  useEffect(() => {
    if (!token || !user) return;

    const expiresIn = user.exp * 1000 - Date.now();
    // Refresh much earlier - when 5 minutes left (instead of 1 minute)
    const refreshTime = Math.max(expiresIn - 5 * 60 * 1000, 5000);
    const scheduledRefreshTime = new Date(Date.now() + refreshTime);
    
    const timeout = setTimeout(() => {
      refresh();
    }, refreshTime);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, user, refresh]);

  // Activity-based token refresh - refresh on user activity
  useEffect(() => {
    if (!token || !user) return;

    const handleUserActivity = () => {
      const expiresIn = user.exp * 1000 - Date.now();
      // If token expires in less than 45 minutes and user is active, refresh it
      if (expiresIn < 60 * 60 * 1000) {
        refresh();
      }
    };

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const throttledHandler = throttle(handleUserActivity, 60000); // Throttle to once per minute

    events.forEach(event => {
      document.addEventListener(event, throttledHandler, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledHandler, true);
      });
    };
  }, [token, user, refresh]);

  // Simple throttle function
  function throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  const contextValue: AuthContextType = {
    token,
    refreshToken,
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    refresh
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;